// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.

import { Suspense, StrictMode } from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./App/App";
import {
  Provider,
  teamsTheme,
  Loader,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from "@fluentui/react-northstar"; //https://fluentsite.z22.web.core.windows.net/quick-start
import * as microsoftTeams from "@microsoft/teams-js";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./App/msal-auth/msalAuth";
import "./Assets/css/styles.scss";
import { GlobalContextProvider } from "./GlobalContextProvider";

microsoftTeams.initialize();

const msalInstance = new PublicClientApplication(msalConfig);

let currentTheme = teamsTheme;
microsoftTeams.registerOnThemeChangeHandler(function (theme) {
  getTeamContext();
  currentTheme =
    theme === "dark"
      ? teamsDarkTheme
      : theme === "contrast"
      ? teamsHighContrastTheme
      : teamsTheme;
});

const getTeamContext = () => {
  microsoftTeams.getContext((context) => {
    // console.log("CONTEXT :::::::::::::::::: ", context);
    document.getElementsByTagName("body")[0].removeAttribute("class");
    document.getElementById("themeDiv").removeAttribute("class");
    document
      .getElementsByTagName("body")[0]
      .classList.add(context?.theme ? context?.theme : "");
    document.getElementById("themeDiv").classList.add("mode");
    document
      .getElementById("themeDiv")
      .classList.add(context?.theme ? context?.theme : "");
  });
};

// console.log(currentTheme?.siteVariables);
window.addEventListener(
  "message",
  function (e) {
    if (
      e?.origin === process.env.REACT_APP_EP_WHITEBOARD &&
      e?.data?.source === "whiteboard"
    ) {
      const msg = this.localStorage.getItem("msg") || "{}";
      const iframeEl = document.getElementsByClassName("frameTag");
      if (msg) {
        iframeEl[0]?.contentWindow?.postMessage(
          {
            source: "whiteboard",
            message: JSON.parse(msg),
          },
          process.env.REACT_APP_EP_WHITEBOARD
        );
      }
    }
  },
  false
);

ReactDOM.render(
  <StrictMode>
    <Provider
      theme={{
        ...currentTheme,
      }}
      style={{ height: "100%" }}
    >
      <Suspense fallback={<Loader style={{ marginTop: "48vh" }} />}>
        <MsalProvider instance={msalInstance}>
          <GlobalContextProvider>
            <App />
          </GlobalContextProvider>
        </MsalProvider>
      </Suspense>
    </Provider>
  </StrictMode>,
  document.getElementById("root")
);

// Disable pointer events on all iframes (dev only)
if (process.env.NODE_ENV === "development") {
  const disableIframeClicks = () => {
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
      // Skip the whiteboard workspace iframe so it remains interactive
      if (
        iframe.id !== "whiteboard-workspace" &&
        iframe.id !== "aiStudio"
      ) {
        iframe.style.pointerEvents = "none";
      }
    });
  };

  // Run once on startup
  disableIframeClicks();

  // Optional: watch for new iframes added dynamically
  const observer = new MutationObserver((mutationsList) => {
    for (let mutation of mutationsList) {
      mutation.addedNodes.forEach((node) => {
        if (node.tagName === "IFRAME") {
          // Keep the whiteboard workspace iframe interactive
          if (
            node.id !== "whiteboard-workspace" &&
            node.id !== "aiStudio"
          ) {
            node.style.pointerEvents = "none";
          }
        }
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
